import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  testId,
  className,
  bodyClassName,
}: {
  title?: ReactNode
  /** Solo un DATO (período, alcance, conteo), nunca una explicación (T1). */
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  testId?: string
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={cn('surface', className)} data-testid={testId}>
      {(title || subtitle || actions) && (
        <header className="surface-divider flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-5 py-3.5">
          <div className="min-w-0">
            {title && <h2 className="font-heading text-base text-foreground">{title}</h2>}
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className={cn('p-5', bodyClassName)}>{children}</div>
    </section>
  )
}

function CardHeader({title, actions}: {title?: ReactNode; actions?: ReactNode}) {
  return (
    <div className="surface-divider flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-5 py-3.5">
      {typeof title === 'string' ? <h2 className="font-heading text-base text-foreground">{title}</h2> : title}
      {actions}
    </div>
  )
}

export function ListCard({
  title,
  actions,
  toolbar,
  footer,
  children,
  testId,
}: {
  title?: ReactNode
  actions?: ReactNode
  toolbar?: ReactNode
  footer?: ReactNode
  children: ReactNode
  testId?: string
}) {
  return (
    // `overflow-hidden` no recorta los menús de fila: viven en un portal de Radix.
    <section className="surface overflow-hidden" data-testid={testId}>
      {(title || actions) && <CardHeader title={title} actions={actions} />}
      {toolbar && (
        <div className="surface-divider flex flex-wrap items-center gap-2 border-b border-dashed px-5 py-3">
          {toolbar}
        </div>
      )}
      {/* 8px + el `pl-3`/`pr-3` de celda = los 20px del encabezado, sin tocar el DataTable. */}
      <div className="px-2 py-1">{children}</div>
      {footer && <div className="px-5 pb-3">{footer}</div>}
    </section>
  )
}

export function ListEmptyState({
  title,
  body,
  action,
  page = false,
}: {
  /** ≤ 8 palabras; dos vacíos opuestos llevan títulos distintos (T4). */
  title: string
  /** En retirada (T4): un vacío es una línea y un botón; no se pasa en pantallas nuevas. */
  body?: string
  /** El paso anterior, nunca una operación irreversible ni un cambio de fase (T4). */
  action?: ReactNode
  page?: boolean
}) {
  return (
    <div className={cn('px-6 text-center', page ? 'py-14' : 'py-10')}>
      <p className={cn('text-foreground', page ? 'font-heading text-lg' : 'text-sm')}>{title}</p>
      {body && <p className="mt-1 text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/** `pct` solo cuando la cifra ES una fracción: una barra inventada mentiría. */
export function StatCard({
  label,
  value,
  detail,
  pct,
  alert = false,
  testId,
}: {
  label: string
  value: ReactNode
  detail?: ReactNode
  pct?: number
  alert?: boolean
  testId?: string
}) {
  return (
    <div className="surface flex flex-col p-5" data-testid={testId}>
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{label}</p>
      <div
        className={cn(
          'mt-2.5 font-mono text-[26px] leading-none font-medium tabular-nums',
          alert ? 'text-destructive' : 'text-foreground'
        )}>
        {value}
      </div>
      {detail && <p className="mt-2 text-xs text-muted-foreground">{detail}</p>}
      {pct !== undefined && (
        <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-track">
          <div className="h-full rounded-full bg-primary" style={{width: `${Math.min(100, Math.max(0, pct))}%`}} />
        </div>
      )}
    </div>
  )
}
